import React, { useState, useContext } from 'react';
import { AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Settings, Bell, FileText, Image } from 'lucide-react';
import NotificationSettings from './NotificationSettings';

const GlobalSettings = () => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('pdf');

  // PDF Settings
  const [pdfSettings, setPdfSettings] = useState({
    companyName: 'Total Facility Solutions',
    companyNameItalic: 'facility forward®',
    logoUrl: 'https://customer-assets.emergentagent.com/job_tfs-workspace/artifacts/xuj2h70u_TFSI%203-26%20PNG.png',
    footerLeft: 'Confidential – Intended for recipient only',
    defaultFont: 'Helvetica'
  });

  const handleSavePdfSettings = () => {
    // In production, this would call API to save settings
    toast.success('PDF settings saved successfully');
  };

  const tabs = [
    { id: 'pdf', label: 'PDF Settings', icon: FileText },
    { id: 'notifications', label: 'Notifications & Reminders', icon: Bell },
    { id: 'system', label: 'System Settings', icon: Settings }
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Global Settings</h2>
        <p className="text-gray-600 mt-1">Configure universal settings for TFS Operations Hub</p>
      </div>

      {user.role !== 'owner' && (
        <Card className="mb-6 bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <p className="text-sm text-yellow-800">
              ⚠️ Only the OWNER can modify global settings. Contact sperez@tfsi-us.com for changes.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Tab Navigation */}
      <div className="flex space-x-2 mb-6 border-b">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`settings-tab-${tab.id}`}
              className={`flex items-center space-x-2 px-4 py-2 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* PDF Settings Tab */}
      {activeTab === 'pdf' && (
        <Card>
          <CardHeader>
            <CardTitle>PDF Generation Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  value={pdfSettings.companyName}
                  onChange={(e) => setPdfSettings({ ...pdfSettings, companyName: e.target.value })}
                  disabled={user.role !== 'owner'}
                />
              </div>

              <div>
                <Label htmlFor="companyNameItalic">Company Name (Italic)</Label>
                <Input
                  id="companyNameItalic"
                  value={pdfSettings.companyNameItalic}
                  onChange={(e) => setPdfSettings({ ...pdfSettings, companyNameItalic: e.target.value })}
                  disabled={user.role !== 'owner'}
                />
                <p className="text-xs text-gray-500 mt-1">Appears below logo on all PDFs</p>
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="logoUrl">Logo URL</Label>
                <Input
                  id="logoUrl"
                  value={pdfSettings.logoUrl}
                  onChange={(e) => setPdfSettings({ ...pdfSettings, logoUrl: e.target.value })}
                  disabled={user.role !== 'owner'}
                />
                <p className="text-xs text-gray-500 mt-1">Logo appears on all PDF headers (max 2-inch height)</p>
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="footerLeft">Footer Text (Left)</Label>
                <Input
                  id="footerLeft"
                  value={pdfSettings.footerLeft}
                  onChange={(e) => setPdfSettings({ ...pdfSettings, footerLeft: e.target.value })}
                  disabled={user.role !== 'owner'}
                />
                <p className="text-xs text-gray-500 mt-1">Appears on bottom-left of every page</p>
              </div>

              <div>
                <Label htmlFor="defaultFont">Default Font</Label>
                <select
                  id="defaultFont"
                  className="w-full border rounded-md px-3 py-2"
                  value={pdfSettings.defaultFont}
                  onChange={(e) => setPdfSettings({ ...pdfSettings, defaultFont: e.target.value })}
                  disabled={user.role !== 'owner'}
                >
                  <option value="Helvetica">Helvetica</option>
                  <option value="Times-Roman">Times Roman</option>
                  <option value="Courier">Courier</option>
                </select>
              </div>
            </div>

            {user.role === 'owner' && (
              <Button onClick={handleSavePdfSettings}>
                Save PDF Settings
              </Button>
            )}

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">PDF Photo Rules</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• All photos display at 3-inch height</li>
                <li>• Aspect ratio is automatically maintained</li>
                <li>• Photos always appear upright (EXIF orientation handled)</li>
                <li>• Optional captions supported</li>
                <li>• Optional photo appendix at end of document</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notifications & Reminders Tab (CP-02) */}
      {activeTab === 'notifications' && (
        <div data-testid="notifications-tab">
          <NotificationSettings />
        </div>
      )}

      {/* System Settings Tab */}
      {activeTab === 'system' && (
        <Card>
          <CardHeader>
            <CardTitle>System Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label>Date Format</Label>
                <div className="w-full border rounded-md px-3 py-2 mt-1 bg-slate-50 text-slate-600 text-sm" data-testid="date-format-standard">
                  MM/DD/YY <span className="text-slate-400">— application-wide standard (fixed)</span>
                </div>
              </div>

              <div>
                <Label>Timezone</Label>
                <select className="w-full border rounded-md px-3 py-2 mt-1" disabled={user.role !== 'owner'}>
                  <option>America/New_York (EST/EDT)</option>
                  <option>America/Chicago (CST/CDT)</option>
                  <option>America/Denver (MST/MDT)</option>
                  <option>America/Los_Angeles (PST/PDT)</option>
                </select>
              </div>

              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">System Information</h4>
                <div className="text-sm text-gray-700 space-y-1">
                  <p>Version: 2.0.0-modular</p>
                  <p>Environment: Production</p>
                  <p>Database: MongoDB</p>
                  <p>Backend: FastAPI</p>
                  <p>Frontend: React</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GlobalSettings;
